import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { CapacitorShareTarget, type ShareReceivedEvent } from '@capgo/capacitor-share-target';
import { canAccessApp, clientDebugLog } from '@apexx/auth';
import { SHARE_TO_CHAT_PATH, hasPendingShare, saveShareReceivedEvent } from '@apexx/share-intent';
import { shellOrchestrator } from '@/shell/orchestrator';
import { ApexxShareInbox } from './apexxShareInbox';

type ShareTargetRouter = {
	push: (to: string | { name: string; query?: Record<string, string> }) => Promise<unknown> | unknown;
	replace: (to: string | { name: string; query?: Record<string, string> }) => Promise<unknown> | unknown;
};

type ShareTargetAuth = {
	isLoggedIn: boolean;
	accessToken?: string;
};

type ShareHandler = (event: ShareReceivedEvent) => Promise<void>;

let listenerRegistered = false;
let queuedShareEvent: ShareReceivedEvent | null = null;
let activeHandler: ShareHandler | null = null;
let inboxPollInFlight = false;
let lastHandledFingerprint = '';
let lastHandledAtMs = 0;

/** Ignore identical Capgo re-delivers within this window; allow intentional re-shares after. */
const DUPLICATE_SHARE_WINDOW_MS = 2500;

function shareLog(
	level: 'info' | 'warn',
	message: string,
	details?: Record<string, unknown>,
): void {
	try {
		clientDebugLog(level, `[hub:share] ${message}`, details);
	} catch {
		// Never let logging break share delivery (Sentry sink must not swallow Capgo events).
		try {
			if (level === 'warn') {
				console.warn(`[hub:share] ${message}`, details);
			} else {
				console.info(`[hub:share] ${message}`, details);
			}
		} catch {
			// ignore
		}
	}
}

function eventFingerprint(event: ShareReceivedEvent): string {
	return JSON.stringify({
		title: event.title ?? '',
		texts: event.texts ?? [],
		files: (event.files ?? []).map((file) => ({
			name: file.name,
			mimeType: file.mimeType,
			uri: file.uri,
		})),
	});
}

function isDuplicateShareEvent(event: ShareReceivedEvent): boolean {
	const fingerprint = eventFingerprint(event);
	if (fingerprint !== lastHandledFingerprint) {
		return false;
	}

	return Date.now() - lastHandledAtMs < DUPLICATE_SHARE_WINDOW_MS;
}

function markShareHandled(event: ShareReceivedEvent): void {
	lastHandledFingerprint = eventFingerprint(event);
	lastHandledAtMs = Date.now();
}

export function canAccessChatShare(accessToken: string | undefined): boolean {
	return canAccessApp(accessToken, 'app:chat');
}

export async function resolvePostAuthShareDestination(
	accessToken: string | undefined,
): Promise<string | undefined> {
	if (!canAccessChatShare(accessToken)) {
		return undefined;
	}

	if (!(await hasPendingShare())) {
		return undefined;
	}

	return SHARE_TO_CHAT_PATH;
}

async function navigateToShareOrLogin(
	router: ShareTargetRouter,
	auth: ShareTargetAuth,
): Promise<void> {
	if (!auth.isLoggedIn) {
		shareLog('info', 'navigating to login with returnTo=share');
		await router.replace({ name: 'login', query: { returnTo: 'share' } });
		return;
	}

	if (!canAccessChatShare(auth.accessToken)) {
		shareLog('warn', 'user lacks app:chat; sending to launcher');
		await router.replace({ name: 'launcher' });
		return;
	}

	shareLog('info', 'navigating to share picker', { path: SHARE_TO_CHAT_PATH });
	await router.replace(SHARE_TO_CHAT_PATH);
	// Hub replace alone is not enough when chat is already mounted: the shell skips
	// remount for the same appId, so explicitly sync the embedded chat router.
	await shellOrchestrator.syncActiveSubPath('chat', 'share');
}

async function handleShareReceived(
	event: ShareReceivedEvent,
	router: ShareTargetRouter,
	getAuth: () => ShareTargetAuth,
): Promise<void> {
	if (isDuplicateShareEvent(event)) {
		shareLog('info', 'ignoring duplicate share event', {
			fileCount: event.files?.length ?? 0,
			textCount: event.texts?.length ?? 0,
		});
		return;
	}

	const materialized = await materializeIosShareFiles(event);
	const hasFiles = (materialized.files?.length ?? 0) > 0;
	const hasText = (materialized.texts?.length ?? 0) > 0;

	shareLog('info', 'received', {
		platform: Capacitor.getPlatform(),
		title: materialized.title,
		textCount: materialized.texts?.length ?? 0,
		fileCount: materialized.files?.length ?? 0,
		files: materialized.files?.map((file) => ({
			name: file.name,
			mimeType: file.mimeType,
			uriPrefix: file.uri?.slice(0, 64),
		})),
	});

	if (!hasFiles && !hasText) {
		shareLog('warn', 'empty share payload, ignoring');
		return;
	}

	try {
		await saveShareReceivedEvent({
			title: materialized.title,
			texts: materialized.texts,
			files: materialized.files,
		});
		shareLog('info', 'persisted pending share');
	} catch (error) {
		shareLog('warn', 'failed to persist files, trying text-only fallback', {
			error: error instanceof Error ? error.message : String(error),
		});

		if (!hasText) {
			throw error;
		}

		await saveShareReceivedEvent({
			title: materialized.title,
			texts: materialized.texts,
			files: [],
		});
		shareLog('info', 'persisted text-only pending share');
	}

	markShareHandled(event);
	await navigateToShareOrLogin(router, getAuth());
}

/**
 * iOS Share Extension / Capgo hand off App Group paths that Filesystem often cannot open.
 * Copy into the app sandbox via ApexxShareInbox before persisting.
 * Fail hard on copy errors — keeping the App Group URI only reproduces readFile failures.
 */
async function materializeIosShareFiles(event: ShareReceivedEvent): Promise<ShareReceivedEvent> {
	if (Capacitor.getPlatform() !== 'ios' || !event.files?.length) {
		return event;
	}

	const files: NonNullable<ShareReceivedEvent['files']> = [];

	for (const file of event.files) {
		const sourceUri = file.uri?.startsWith('/') ? `file://${file.uri}` : file.uri;

		try {
			const copied = await ApexxShareInbox.copySharedFile({
				sourceUri,
				fileName: file.name,
			});
			files.push({
				uri: copied.uri,
				name: copied.name || file.name,
				mimeType: file.mimeType,
			});
			shareLog('info', 'copied ios shared file into app sandbox', {
				name: copied.name || file.name,
				size: copied.size,
			});
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			shareLog('warn', 'ios native copy failed', {
				name: file.name,
				error: message,
			});
			throw new Error(`iOS share file copy failed for ${file.name}: ${message}`);
		}
	}

	return {
		...event,
		files,
	};
}

function deliverShareEvent(event: ShareReceivedEvent): void {
	if (activeHandler) {
		void activeHandler(event);
		return;
	}

	shareLog('info', 'queuing event until hub bootstrap finishes', {
		fileCount: event.files?.length ?? 0,
		textCount: event.texts?.length ?? 0,
	});
	// Capgo can emit the retained payload more than once; keep the latest.
	queuedShareEvent = event;
}

/**
 * iOS Share Extension writes App Group UserDefaults; Capgo often misses the handoff.
 * Poll our hub-owned inbox plugin on share deep links / foreground.
 */
async function pollIosShareInbox(reason: string): Promise<void> {
	if (Capacitor.getPlatform() !== 'ios' || inboxPollInFlight) {
		return;
	}

	inboxPollInFlight = true;

	try {
		const result = await ApexxShareInbox.consume();
		shareLog('info', 'ios inbox poll', {
			reason,
			found: result.found,
			emptyReason: result.reason,
			appGroupId: result.appGroupId,
			key: result.key,
			fileCount: result.fileCount ?? 0,
			textCount: result.textCount ?? 0,
		});

		if (!result.found) {
			return;
		}

		deliverShareEvent({
			title: result.title ?? '',
			texts: result.texts ?? [],
			files: (result.files ?? []).map((file) => ({
				uri: file.uri,
				name: file.name,
				mimeType: file.mimeType,
			})),
		});
	} catch (error) {
		shareLog('warn', 'ios inbox poll failed', {
			reason,
			error: error instanceof Error ? error.message : String(error),
		});
	} finally {
		inboxPollInFlight = false;
	}
}

function isShareDeepLink(url: string | undefined): boolean {
	if (!url) {
		return false;
	}

	try {
		const parsed = new URL(url);
		return parsed.host === 'share' || parsed.pathname === '/share' || parsed.pathname.endsWith('/share');
	} catch {
		return url.includes('://share');
	}
}

/**
 * Register Capgo as early as possible so Android retained SEND intents are not lost.
 * iOS also polls the App Group inbox because Capgo often misses extension handoffs.
 */
export async function setupShareTargetListenerEarly(): Promise<void> {
	if (!Capacitor.isNativePlatform() || listenerRegistered) {
		return;
	}

	listenerRegistered = true;
	shareLog('info', 'registering share listeners', { platform: Capacitor.getPlatform() });

	await CapacitorShareTarget.addListener('shareReceived', (event) => {
		// Deliver first — logging must never prevent Capgo event handling.
		try {
			deliverShareEvent(event);
		} finally {
			shareLog('info', 'capgo shareReceived', {
				fileCount: event.files?.length ?? 0,
				textCount: event.texts?.length ?? 0,
			});
		}
	});

	if (Capacitor.getPlatform() === 'ios') {
		void ApexxShareInbox.peek()
			.then((result) => {
				shareLog('info', 'ios share inbox plugin ready', {
					found: result.found,
					emptyReason: result.reason,
					appGroupId: result.appGroupId,
				});
			})
			.catch((error: unknown) => {
				shareLog('warn', 'ios share inbox plugin unavailable', {
					error: error instanceof Error ? error.message : String(error),
				});
			});

		await App.addListener('appUrlOpen', (event) => {
			shareLog('info', 'appUrlOpen', { url: event.url, isShare: isShareDeepLink(event.url) });

			if (isShareDeepLink(event.url)) {
				void pollIosShareInbox('appUrlOpen');
			}
		});

		await App.addListener('appStateChange', (state) => {
			if (!state.isActive) {
				return;
			}

			shareLog('info', 'app became active; polling ios share inbox');
			void pollIosShareInbox('appStateChange');
		});

		void pollIosShareInbox('setup-early');
		void App.getLaunchUrl()
			.then((launch) => {
				shareLog('info', 'getLaunchUrl', {
					url: launch?.url,
					isShare: isShareDeepLink(launch?.url),
				});

				if (isShareDeepLink(launch?.url)) {
					void pollIosShareInbox('getLaunchUrl');
				}
			})
			.catch((error: unknown) => {
				shareLog('warn', 'getLaunchUrl failed', {
					error: error instanceof Error ? error.message : String(error),
				});
			});
	}
}

/**
 * Bind router/auth handling after bootstrap. Drains any queued cold-start share.
 * Errors are swallowed so a failed share never blocks app.mount().
 */
export async function bindShareTargetHandler(options: {
	router: ShareTargetRouter;
	getAuth: () => ShareTargetAuth;
}): Promise<void> {
	if (!Capacitor.isNativePlatform()) {
		return;
	}

	activeHandler = async (event) => {
		try {
			await handleShareReceived(event, options.router, options.getAuth);
		} catch (error) {
			shareLog('warn', 'Failed to handle inbound share', {
				error: error instanceof Error ? error.message : String(error),
			});
		}
	};

	shareLog('info', 'share handler bound', {
		hasQueuedEvent: queuedShareEvent !== null,
	});

	if (queuedShareEvent) {
		const event = queuedShareEvent;
		queuedShareEvent = null;
		await activeHandler(event);
	}

	if (Capacitor.getPlatform() === 'ios') {
		void pollIosShareInbox('handler-bound');
	}
}

/**
 * @deprecated Prefer setupShareTargetListenerEarly + bindShareTargetHandler.
 * Kept as a single-call convenience for tests.
 */
export async function setupShareTarget(options: {
	router: ShareTargetRouter;
	getAuth: () => ShareTargetAuth;
}): Promise<void> {
	await setupShareTargetListenerEarly();
	await bindShareTargetHandler(options);
}

/** Resume a pending share after successful login. */
export async function resumePendingShareIfAny(
	router: ShareTargetRouter,
	accessToken: string | undefined,
): Promise<boolean> {
	const destination = await resolvePostAuthShareDestination(accessToken);

	if (!destination) {
		shareLog('info', 'no pending share to resume after auth');
		return false;
	}

	shareLog('info', 'resuming pending share after auth', { path: destination });
	await router.replace(destination);
	await shellOrchestrator.syncActiveSubPath('chat', 'share');

	return true;
}
